import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaCode, FaCamera, FaLaptopCode, FaUserGraduate } from 'react-icons/fa';

const About = () => {
  const [sectionRef, inView] = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });

  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
        when: 'beforeChildren',
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.6, ease: 'easeOut' }
    },
    hover: {
      scale: 1.05,
      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      transition: { duration: 0.3 }
    }
  };

  const iconVariants = {
    hidden: { opacity: 0, rotate: -45, scale: 0.5 },
    visible: {
      opacity: 1, 
      rotate: 0,
      scale: 1,
      transition: { 
        duration: 0.4, 
        ease: 'easeOut', 
        type: 'spring',
        stiffness: 200
      }
    }
  };

  return (
    <section id="about" className="py-20 relative">
      {/* 背景裝飾 */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute -top-48 -right-48 w-96 h-96 rounded-full bg-primary-900/20 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-secondary-900/20 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          ref={sectionRef}
          variants={sectionVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="max-w-5xl mx-auto"
        >
          <motion.div variants={itemVariants} className="text-center mb-16">
            <motion.h2 
              variants={itemVariants} 
              className="text-3xl md:text-4xl font-bold mb-4 inline-block"
            >
              關於<span className="text-gradient">我</span>
            </motion.h2>
            <motion.div variants={itemVariants} className="w-20 h-1 bg-gradient-to-r from-primary-500 to-secondary-500 mx-auto mb-8"></motion.div>
            <motion.p variants={itemVariants} className="text-lg text-gray-300 max-w-2xl mx-auto">
              我是一名充滿熱情的資訊工程師，專注於創造高品質的數位體驗和解決方案。   
              透過不斷學習和實踐，我致力於在技術領域中不斷成長，並為用戶帶來價值。
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
            {/* 左側個人資訊 */}
            <motion.div variants={itemVariants} className="space-y-6 md:space-y-8">
              <motion.div 
                variants={cardVariants}
                whileHover="hover"
                className="card"
              >
                <h3 className="text-xl font-semibold mb-4 text-primary-300">個人簡介</h3>
                <p className="text-gray-300">
                  我是胡晉愷，現職資訊工程師，畢業於國立高雄科技大學 電腦與通訊工程系碩士。熱愛編寫乾淨、高效的程式碼，並且對新技術充滿好奇心。我注重負責、積極努力，有良好的團隊合作精神，並擅長解決問題。
                </p>
              </motion.div>

              <motion.div
                variants={cardVariants}
                whileHover="hover"
                className="card"
              >
                <h3 className="text-xl font-semibold mb-4 text-primary-300">專業背景</h3>
                <p className="text-gray-300">
                  我擅長多種程式語言與技術，包括Java、C、C++、C#、Python和網頁開發技術，深度學習與機器學習，以及Android與iOS應用開發。曾參與NFT門票系統開發，熟悉影像辨識技術，並有豐富的項目經驗。
                </p>
              </motion.div>
              
              <motion.div
                variants={cardVariants}
                whileHover="hover"
                className="card"
              >
                <h3 className="text-xl font-semibold mb-4 text-primary-300">個人態度</h3>
                <p className="text-gray-300">
                  職業特質：負責任、積極努力、專注學習、專題協作。我熟悉多種程式語言與開發環境，擁有解決問題的思維與目標導向的工作態度。我喜歡基於程式與工程的解決方案，創造出適合自己及市場的應用。
                </p>
              </motion.div>
            </motion.div>

            {/* 右側興趣與專長 */}
            <motion.div variants={itemVariants}>
              <motion.div 
                variants={cardVariants}
                whileHover="hover"
                className="card h-full flex flex-col"
              >
                <h3 className="text-xl font-semibold mb-6 text-primary-300">興趣與專長</h3>
                
                <div className="grid grid-cols-1 gap-6 flex-1">
                  <motion.div 
                    className="flex items-start gap-4"
                    variants={itemVariants}  
                  >
                    <motion.div
                      variants={iconVariants}
                      className="bg-primary-900/40 text-primary-400 p-3 rounded-lg"
                    >
                      <FaCode className="text-xl" />
                    </motion.div>
                    <div>
                      <h4 className="text-lg font-medium mb-1 text-white">程式開發</h4>
                      <p className="text-gray-300">
                        熱衷於軟體開發，特別是網頁和行動應用程式，享受將想法轉變為實際產品的過程。
                      </p>
                    </div>
                  </motion.div>

                  <motion.div 
                    className="flex items-start gap-4"
                    variants={itemVariants}  
                  >
                    <motion.div
                      variants={iconVariants}
                      className="bg-primary-900/40 text-primary-400 p-3 rounded-lg"
                    >
                      <FaCamera className="text-xl" />
                    </motion.div>
                    <div>
                      <h4 className="text-lg font-medium mb-1 text-white">攝影</h4>
                      <p className="text-gray-300">
                        喜歡通過鏡頭捕捉獨特的瞬間，探索不同的攝影風格和技術，分享生活中的美好。
                      </p>
                    </div>
                  </motion.div>

                  <motion.div 
                    className="flex items-start gap-4"
                    variants={itemVariants}  
                  >
                    <motion.div
                      variants={iconVariants}
                      className="bg-primary-900/40 text-primary-400 p-3 rounded-lg"
                    >
                      <FaLaptopCode className="text-xl" />
                    </motion.div>
                    <div>
                      <h4 className="text-lg font-medium mb-1 text-white">AI應用開發</h4>
                      <p className="text-gray-300">
                        深入研究人工智慧與深度學習技術，致力於將AI技術應用於實際項目中，解決實際問題。
                      </p>
                    </div>
                  </motion.div>

                  <motion.div 
                    className="flex items-start gap-4"
                    variants={itemVariants}  
                  >
                    <motion.div
                      variants={iconVariants}
                      className="bg-primary-900/40 text-primary-400 p-3 rounded-lg"
                    >
                      <FaUserGraduate className="text-xl" />
                    </motion.div>
                    <div>
                      <h4 className="text-lg font-medium mb-1 text-white">持續學習</h4>
                      <p className="text-gray-300">
                        不斷學習新技術和最佳實踐，跟上快速發展的技術趨勢，提升自己的專業技能。
                      </p>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
